(() => {
  const defaultConfig = {
    imageUrl: '',
    width: 84,
    height: 84,
    offsetX: 0,
    offsetY: -8,
    hideWhenChatOpen: true,
    zIndex: 2147483647,
    debug: true,
    mobileDockVisible: {
      enabled: false,
      maxWidth: 767,
      bodyHasSelector: '',
      left: '',
      bottom: '',
      width: null,
      height: null,
      offsetX: null,
      offsetY: null,
      zIndex: null,
    },
    mobileDockDefault: {
      enabled: false,
      maxWidth: 767,
      bodyMissingSelector: '',
      left: '',
      bottom: '',
      width: null,
      height: null,
      offsetX: null,
      offsetY: null,
      zIndex: null,
    },
  };
  const suppliedConfig = window.GORGIAS_CHAT_OVERLAY || {};
  const config = {
    ...defaultConfig,
    ...suppliedConfig,
    mobileDockVisible: {
      ...defaultConfig.mobileDockVisible,
      ...(suppliedConfig.mobileDockVisible || {}),
    },
    mobileDockDefault: {
      ...defaultConfig.mobileDockDefault,
      ...(suppliedConfig.mobileDockDefault || {}),
    },
  };

  const log = (event, details) => {
    if (!config.debug) return;
    if (details === undefined) {
      console.log('[Gorgias Chat Overlay]', event);
    } else {
      console.log('[Gorgias Chat Overlay]', event, details);
    }
  };

  if (!config.imageUrl) {
    log('No imageUrl configured. Set window.GORGIAS_CHAT_OVERLAY.imageUrl.');
    return;
  }

  const OVERLAY_ID = 'gorgias-chat-button-overlay';
  let overlay = null;
  let observer = null;
  let frameObserver = null;
  let observedFrame = null;
  let rafToken = null;
  let missingFrameTimer = null;
  let lastFrameStatus = null;
  let lastRenderSnapshot = '';

  function findFrame() {
    return document.querySelector('iframe#chat-button');
  }

  function bodyHasSelector(selector) {
    return Boolean(selector && document.body?.querySelector(selector));
  }

  function getActiveMobileDockMode() {
    const visibleMode = config.mobileDockVisible;
    const withinVisibleWidth = window.innerWidth <= Number(visibleMode.maxWidth || 767);

    if (visibleMode.enabled && withinVisibleWidth && bodyHasSelector(visibleMode.bodyHasSelector)) {
      return { name: 'mobileDockVisible', settings: visibleMode };
    }

    const defaultMode = config.mobileDockDefault;
    if (!defaultMode.enabled || window.innerWidth > Number(defaultMode.maxWidth || 767)) return null;

    const selector = defaultMode.bodyMissingSelector || visibleMode.bodyHasSelector;
    if (selector && bodyHasSelector(selector)) return null;

    return { name: 'mobileDockDefault', settings: defaultMode };
  }

  function observeFrame(frame) {
    if (observedFrame === frame) return;
    if (frameObserver) frameObserver.disconnect();

    observedFrame = frame;
    frameObserver = null;
    if (!frame) return;

    // Gorgias moves/resizes the launcher by changing inline styles. Observe
    // only the iframe itself to avoid noisy mutations from the rest of its UI.
    frameObserver = new MutationObserver(scheduleUpdate);
    frameObserver.observe(frame, {
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'aria-expanded', 'data-open'],
    });
    log('Observing Gorgias launcher position changes');
  }

  function ensureOverlay() {
    if (overlay?.isConnected) return overlay;

    overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) {
      overlay = document.createElement('img');
      overlay.id = OVERLAY_ID;
      overlay.src = config.imageUrl;
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.addEventListener('load', () => {
        log('Image loaded', { naturalWidth: overlay.naturalWidth, naturalHeight: overlay.naturalHeight });
      }, { once: true });
      overlay.addEventListener('error', () => {
        log('Image failed to load', { imageUrl: config.imageUrl });
      }, { once: true });
      Object.assign(overlay.style, {
        position: 'fixed',
        width: `${config.width}px`,
        height: `${config.height}px`,
        objectFit: 'contain',
        pointerEvents: 'none',
        display: 'none',
        zIndex: String(config.zIndex),
      });
      document.body.appendChild(overlay);
      log('Created image overlay', { imageUrl: config.imageUrl });
    }

    return overlay;
  }

  function updateOverlay() {
    rafToken = null;
    const frame = findFrame();
    observeFrame(frame);

    if (!frame || !document.body) {
      if (overlay) overlay.style.display = 'none';
      if (lastFrameStatus !== 'missing') {
        lastFrameStatus = 'missing';
        log('iframe#chat-button not found; overlay hidden');
      }
      // Gorgias may remove the launcher while the conversation is open and
      // recreate it on close. Keep checking quietly in case that remount is
      // not reported through the observed document subtree.
      if (!missingFrameTimer) {
        missingFrameTimer = window.setInterval(() => {
          if (findFrame()) {
            window.clearInterval(missingFrameTimer);
            missingFrameTimer = null;
            log('iframe#chat-button returned; restoring overlay');
            scheduleUpdate();
          }
        }, 500);
      }
      return;
    }

    if (missingFrameTimer) {
      window.clearInterval(missingFrameTimer);
      missingFrameTimer = null;
    }

    if (lastFrameStatus !== 'found') {
      lastFrameStatus = 'found';
      log('Found iframe#chat-button', {
        title: frame.title,
        className: frame.className,
        srcdoc: Boolean(frame.srcdoc),
      });
    }

    const rect = frame.getBoundingClientRect();
    const frameStyle = window.getComputedStyle(frame);
    const isVisible = rect.width > 0
      && rect.height > 0
      && frameStyle.display !== 'none'
      && frameStyle.visibility !== 'hidden'
      && Number(frameStyle.opacity) !== 0;

    // A page script generally cannot inspect a closed root inside the iframe.
    // Keep this check for installations that expose state on the iframe itself.
    const isChatOpen = frame.dataset.open === 'true';
    const shouldHide = !isVisible || (config.hideWhenChatOpen && isChatOpen);

    if (shouldHide) {
      const image = ensureOverlay();
      image.style.display = 'none';
      const reason = !isVisible ? 'iframe is not visible' : 'chat is open';
      if (lastRenderSnapshot !== `hidden:${reason}`) {
        lastRenderSnapshot = `hidden:${reason}`;
        log('Overlay hidden', { reason });
      }
      return;
    }

    const image = ensureOverlay();
    const mobileMode = getActiveMobileDockMode();
    const mode = mobileMode?.settings;
    const width = Number(mode?.width ?? config.width);
    const height = Number(mode?.height ?? config.height);
    const offsetX = Number(mode?.offsetX ?? config.offsetX ?? 0);
    const offsetY = Number(mode?.offsetY ?? config.offsetY ?? -8);
    const left = mode?.left
      ? null
      : Math.round(rect.left + rect.width / 2 - width / 2 + offsetX);
    const top = mode?.bottom
      ? null
      : Math.round(rect.bottom - height + offsetY);

    image.style.width = `${width}px`;
    image.style.height = `${height}px`;
    image.style.zIndex = String(mode?.zIndex ?? config.zIndex);
    image.style.left = mode?.left || `${left}px`;
    image.style.right = 'auto';
    image.style.top = top === null ? 'auto' : `${top}px`;
    image.style.bottom = mode?.bottom
      ? `calc(${mode.bottom} + ${-offsetY}px)`
      : 'auto';
    image.style.display = 'block';

    const snapshot = `shown:${mobileMode?.name || 'default'}:${image.style.left}:${image.style.top}:${image.style.bottom}:${width}:${height}`;
    if (lastRenderSnapshot !== snapshot) {
      lastRenderSnapshot = snapshot;
      log('Overlay positioned and shown', {
        left: image.style.left,
        top: image.style.top,
        bottom: image.style.bottom,
        width,
        height,
        zIndex: image.style.zIndex,
        mode: mobileMode?.name || 'default',
        offsetY,
        iframeRect: {
          left: Math.round(rect.left),
          top: Math.round(rect.top),
          right: Math.round(rect.right),
          bottom: Math.round(rect.bottom),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
        },
        imageComplete: image.complete,
        imageNaturalWidth: image.naturalWidth,
      });
    }
  }

  function scheduleUpdate() {
    if (rafToken !== null) return;
    rafToken = window.requestAnimationFrame(updateOverlay);
  }

  function init() {
    log('Initializing', {
      imageUrl: config.imageUrl,
      hideWhenChatOpen: config.hideWhenChatOpen,
    });

    observer = new MutationObserver(scheduleUpdate);
    // Only watch for elements being mounted/removed. Gorgias frequently changes
    // attributes inside its widget, which would otherwise create noisy logs.
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('orientationchange', scheduleUpdate, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate, { passive: true });
      window.visualViewport.addEventListener('scroll', scheduleUpdate, { passive: true });
    }

    log('Child-list observer and window listeners attached');
    scheduleUpdate();
  }

  if (document.readyState === 'loading') {
    log('Waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
